import React from 'react';
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { resolveModuleForPath } from '@/utils/analytics/taxonomy';
import { buildScrubbedUrl } from '@/utils/analytics/scrubUrlProperties';
import { capture } from '@/utils/analytics/capture';

// Create Custom Sentry Route component
const SentryRoute = Sentry.withSentryRouting(Route);

// Module-level, not per-mount: React Router v5 re-renders (and can
// remount) matched routes frequently, and this must fire once per
// navigation, not once per render.
let lastCapturedPathname: string | undefined;

/**
 * posthog-js stamps every event with `$current_url` / `$pathname` from
 * window.location — at FLUSH time (plan 010's queue), i.e. from whatever
 * page is open when the batch goes out, not the page this pageview is
 * about. Manager URLs also embed company/project slugs. So every pageview
 * sets both explicitly, at capture time, to the de-slugged route pattern:
 * `$pathname = /machine/:id`, `$current_url = origin + basename + pattern`
 * (BIL-5257). Explicit properties win over the SDK's own stamping, and the
 * `before_send` scrub (scrubUrlProperties.ts) maps everything else the SDK
 * adds the same way.
 */
const capturePageviewOnce = (pathname: string): void => {
  if (pathname === lastCapturedPathname) return;
  lastCapturedPathname = pathname;
  try {
    const { module, routeName, routePattern } = resolveModuleForPath(pathname);
    const pattern = routePattern ?? '/';
    capture('$pageview', {
      module,
      route_name: routeName,
      $pathname: pattern,
      $current_url: buildScrubbedUrl(pattern),
    });
  } catch {
    // Never let a capture failure break routing.
  }
};

/**
 * The capture runs in an effect, not in the Route's render callback: React
 * can abandon or repeat render work before committing, so a capture during
 * render could emit a pageview for a route that never showed (and, through
 * the module-level dedupe, suppress the one that did). An effect only runs
 * for a committed navigation. Rendered inside the matched route so it
 * still fires only when this route actually matches.
 */
const PageviewCapture = ({ pathname }: { pathname: string }) => {
  React.useEffect(() => {
    capturePageviewOnce(pathname);
  }, [pathname]);
  return null;
};

// Route (and therefore Sentry.withSentryRouting(Route)) is a generic
// component whose JSX usages across this codebase pass extra, ad hoc props
// (e.g. `title`) that RouteProps doesn't declare — Route's own generic `T`
// absorbs them per call site. A plain `RouteProps` here would reject those
// call sites, so this stays loose the same way.
type SentryTracedRouteProps = RouteProps &
  Record<string, unknown> & {
    /**
     * `false` for redirect-only routes (`/login`, the slash-normalisation
     * handlers): they render no screen, so a pageview from them is noise —
     * and, in maquinista, an `unknown` module. Defaults to true.
     */
    trackPageview?: boolean;
  };

/**
 * Wraps Sentry.withSentryRouting(Route) — kept exactly as-is — to also emit
 * one PostHog $pageview per navigation, for every route in both apps (see
 * plan 009 chokepoint 1). Accepts the same props as react-router-dom's
 * Route (path/exact/strict/component/render/children); whichever render
 * strategy the call site uses, the underlying Route still only invokes it
 * when this route actually matches, so pageview capture stays tied to real
 * navigations, not to every prop pass.
 */
export const SentryTracedRoute: React.FC<SentryTracedRouteProps> = ({
  component: Component,
  render,
  children,
  trackPageview = true,
  ...rest
}) => (
  <SentryRoute
    {...rest}
    render={(routeProps: RouteComponentProps) => (
      <>
        {trackPageview && (
          <PageviewCapture pathname={routeProps.location.pathname} />
        )}
        {Component ? (
          <Component {...routeProps} />
        ) : typeof render === 'function' ? (
          render(routeProps)
        ) : (
          children
        )}
      </>
    )}
  />
);
